import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { L10nTranslationService } from 'angular-l10n';
import { QueryConfig } from '@wonder-bi-front/query-engine/interfaces/query.interface';
import { AIActions } from './ai.actions';
import { AssistantPersona, ThinkingLevel } from './ai-chat.types';
import {
  selectAILoading,
  selectConversationId,
  selectFailedChartConfig,
  selectFailedContext,
  selectFailedErrorCode,
  selectFailedErrorDetail,
  selectCancelledMessage,
  selectFailedMessage,
  selectFailedQueryConfig,
  selectConversations,
  selectGeneratedChartConfig,
  selectGeneratedQueryConfig,
  selectHttpResult,
  selectMessages,
  selectSuggestions,
  selectAvailableModels,
  selectSelectedModel,
  selectDefaultModel,
  selectThinkingLevels,
  selectSqlFirst,
  selectModelSelectorEnabled,
  selectSelectedThinkingLevel,
  selectSessionRating,
} from './ai.selectors';

@Injectable({ providedIn: 'root' })
export class AIService {
  private readonly store = inject(Store);
  private readonly translation = inject(L10nTranslationService);

  // ── Streaming signals (not persisted in store) ────────────────────────────
  private readonly _streamingContent = signal('');
  readonly streamingContent = this._streamingContent.asReadonly();

  // ── UI: history side menu open state (held here so it survives the chat
  //     component being re-created when the focus workspace promotes panels).
  private readonly _historyOpen = signal(false);
  readonly historyOpen = this._historyOpen.asReadonly();

  toggleHistory(): void {
    this._historyOpen.update((v) => !v);
  }

  appendStreamToken(delta: string): void {
    this._streamingContent.update((c) => c + delta);
  }

  clearStreaming(): void {
    this._streamingContent.set('');
  }

  // ── Chat signals ──────────────────────────────────────────────────────────
  readonly messages = this.store.selectSignal(selectMessages);
  readonly conversationId = this.store.selectSignal(selectConversationId);
  /** The user's past conversations for the history sidebar. */
  readonly conversations = this.store.selectSignal(selectConversations);
  readonly generatedQueryConfig = this.store.selectSignal(selectGeneratedQueryConfig);
  readonly generatedChartConfig = this.store.selectSignal(selectGeneratedChartConfig);
  readonly suggestions = this.store.selectSignal(selectSuggestions);
  /** LLMs the user may switch to; empty unless they hold AI_MODEL_SELECT. */
  readonly availableModels = this.store.selectSignal(selectAvailableModels);
  readonly selectedModel = this.store.selectSignal(selectSelectedModel);
  private readonly defaultModel = this.store.selectSignal(selectDefaultModel);
  /** Thinking levels a turn can be run at; empty unless the user holds AI_MODEL_SELECT. */
  readonly thinkingLevels = this.store.selectSignal(selectThinkingLevels);
  /** Whether the assistant is answering through the SQL-first path. */
  readonly sqlFirst = this.store.selectSignal(selectSqlFirst);
  /** Whether the model selector is on at all (AI_MODEL_SELECTOR); off, each agent runs on its own model. */
  readonly modelSelectorEnabled = this.store.selectSignal(selectModelSelectorEnabled);
  /** Level picked for the conversation; null means the provider's own default. */
  readonly selectedThinkingLevel = this.store.selectSignal(selectSelectedThinkingLevel);
  /** The score the user gave this session, 1-5; null while unrated. */
  readonly sessionRating = this.store.selectSignal(selectSessionRating);
  /** Prompt of a turn the user stopped, handed back so it can be resent or reworded. */
  readonly cancelledMessage = this.store.selectSignal(selectCancelledMessage);
  readonly failedMessage = this.store.selectSignal(selectFailedMessage);
  readonly failedQueryConfig = this.store.selectSignal(selectFailedQueryConfig);
  readonly failedChartConfig = this.store.selectSignal(selectFailedChartConfig);
  readonly failedContext = this.store.selectSignal(selectFailedContext);
  readonly failedErrorCode = this.store.selectSignal(selectFailedErrorCode);
  readonly failedErrorDetail = this.store.selectSignal(selectFailedErrorDetail);
  get messages$() {
    return this.store.select(selectMessages);
  }
  get conversationId$() {
    return this.store.select(selectConversationId);
  }
  get generatedQueryConfig$() {
    return this.store.select(selectGeneratedQueryConfig);
  }

  sendMessage(
    message: string,
    currentQueryConfig?: Record<string, unknown>,
    currentChartConfig?: Record<string, unknown>,
    context?: 'query' | 'chart'
  ): void {
    this.store.dispatch(
      AIActions.sendMessage({
        message,
        currentQueryConfig,
        currentChartConfig,
        context,
        model: this.pickedModel(),
        thinkingLevel: this.pickedThinkingLevel(),
      })
    );
  }

  /** Score the finished session from 1 to 5 — the quality signal the offline eval cannot give. */
  rateSession(rating: number): void {
    this.store.dispatch(AIActions.rateSession({ rating }));
  }

  /** Load the catalog of selectable models (only for users allowed to pick one). */
  loadModels(): void {
    this.store.dispatch(AIActions.loadModels());
  }

  setModel(model: string): void {
    this.store.dispatch(AIActions.selectModel({ model }));
  }

  /** Pick how hard the model thinks; null hands the decision back to the provider. */
  setThinkingLevel(thinkingLevel: ThinkingLevel | null): void {
    this.store.dispatch(AIActions.selectThinkingLevel({ thinkingLevel }));
  }

  /**
   * The model to send with a turn, sent only once the user moves off the default. Staying silent
   * otherwise matters twice over: the server rejects the field for users without AI_MODEL_SELECT,
   * and an explicitly pinned model disables the provider fallback on the server side.
   */
  private pickedModel(): string | undefined {
    if (!this.modelSelectorEnabled()) return undefined;
    const model = this.selectedModel();
    return model && model !== this.defaultModel() ? model : undefined;
  }

  /**
   * The level to send with a turn, sent only once the user picks one: staying silent is what leaves
   * the provider's own default in place, and the server refuses the field without AI_MODEL_SELECT.
   */
  private pickedThinkingLevel(): ThinkingLevel | undefined {
    if (!this.modelSelectorEnabled()) return undefined;
    return this.selectedThinkingLevel() ?? undefined;
  }

  /** Stop button: aborts the turn in flight and hands its prompt back to the composer. */
  cancelMessage(): void {
    this.store.dispatch(AIActions.cancelMessage());
  }

  retryMessage(): void {
    const msg = this.failedMessage();
    if (msg)
      this.store.dispatch(
        AIActions.sendMessage({
          message: msg,
          currentQueryConfig: this.failedQueryConfig() ?? undefined,
          currentChartConfig: this.failedChartConfig() ?? undefined,
          context: this.failedContext() ?? undefined,
          model: this.pickedModel(),
          thinkingLevel: this.pickedThinkingLevel(),
        })
      );
  }

  /**
   * The assistant the panel is showing (WB-154). The global assistant keeps it up to date; here it
   * only picks the welcome and the ready-made phrases a new chat starts with.
   */
  private persona: AssistantPersona = 'orchestrator';

  /**
   * Switch the assistant the panel is showing. A chat nobody has written in yet gets the new one's
   * welcome and phrases: opening a builder, or being answered by the guide, changes who is talking.
   */
  setPersona(persona: AssistantPersona): void {
    const changed = persona !== this.persona;
    this.persona = persona;
    const messages = this.messages();
    const untouched = messages.length === 1 && messages[0].role === 'assistant' && !this.conversationId();
    if (changed && untouched) {
      this.store.dispatch(AIActions.startNewConversation());
      this.addWelcomeMessage();
    }
  }

  /** The welcome is the general assistant's, whichever persona's words it carries: it greets the user. */
  addWelcomeMessage(): void {
    const welcome: import('./ai-chat.types').ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: this.translation.translate(`$ai.persona.${this.persona}.welcome`),
      createdAt: new Date().toISOString(),
      answeredBy: 'orchestrator',
    };
    this.store.dispatch(
      AIActions.sendMessageSuccess({ conversationId: '', newMessages: [welcome] })
    );
    this.store.dispatch(AIActions.loadSuggestions({ agent: this.persona }));
  }



  /** Adds turns that never reach the agent (the global assistant's own answers) to the visible conversation. */
  appendLocalMessages(messages: import('./ai-chat.types').ChatMessage[]): void {
    this.store.dispatch(AIActions.appendLocalMessages({ messages }));
  }

  removeMessage(id: string): void {
    this.store.dispatch(AIActions.removeMessage({ id }));
  }

  /** Delete the current conversation (server + history list) and reset to a fresh chat. */
  clearConversation(): void {
    this.clearStreaming();
    // Capture the id now: the reducer nulls conversationId synchronously, so the effect
    // (which performs the server delete + list refresh) reads it from the action payload.
    this.store.dispatch(AIActions.clearConversation({ conversationId: this.conversationId() }));
    this.addWelcomeMessage();
  }

  // ── Conversation history ────────────────────────────────────────────────
  /** Fetch the user's past conversations for the history sidebar. */
  loadConversations(): void {
    this.store.dispatch(AIActions.loadConversations());
  }

  /** Resume a past conversation by id. */
  loadConversation(conversationId: string): void {
    if (conversationId === this.conversationId()) return;
    this.clearStreaming();
    this.store.dispatch(AIActions.loadConversation({ conversationId }));
  }

  /** Start a fresh chat, keeping the current one in history (no server delete). */
  startNewChat(): void {
    this.clearStreaming();
    this.store.dispatch(AIActions.startNewConversation());
    this.addWelcomeMessage();
  }

  applyGeneratedQueryConfig(queryConfig: QueryConfig): void {
    this.store.dispatch(AIActions.applyGeneratedQueryConfig({ queryConfig }));
  }

  /** Deterministically build a chart config for the given saved queries (the "Generate chart" button). */
  generateChartConfig(description: string, queryIds: string[]): void {
    this.store.dispatch(AIActions.generateChartConfig({ description, queryIds }));
  }

  readonly loading = this.store.selectSignal(selectAILoading);
  readonly httpResult = this.store.selectSignal(selectHttpResult);

  /**
   * i18n key describing what the agent is doing right now, derived from the running tool call so the
   * "thinking" spinner can show a step-specific label ("Generando consulta", "Comprobando resultados"…)
   * instead of a static "Pensando…". Falls back to the generic key when no tool is in flight.
   */
  readonly thinkingStepKey = computed(() => {
    const msgs = this.messages();
    let currentTool: string | undefined;
    // Query outcomes seen so far, snapshotted at the moment the current tool call was issued (i.e. BEFORE
    // that call's own result) so we can tell a first generation from a correction or a refinement.
    let priorFailed = false;
    let priorSuccess = false;
    let snapFailed = false;
    let snapSuccess = false;
    for (const m of msgs) {
      if (m.role === 'assistant' && m.toolCall) {
        currentTool = m.toolCall.name;
        snapFailed = priorFailed;
        snapSuccess = priorSuccess;
      } else if (m.role === 'tool') {
        if (m.queryConfig) {
          if (m.toolResult?.ok === false) priorFailed = true;
          else if (m.toolResult?.ok === true) priorSuccess = true;
        } else if (m.toolResult?.ok === false) {
          priorFailed = true; // bare generation/chart failure
        }
      }
    }
    const base = '$ai.chat.step.';
    switch (currentTool) {
      case 'find_existing_query':
        return `${base}searching`;
      case 'execute_query':
        return `${base}checking`;
      case 'generate_chart_config':
        return `${base}charting`;
      case 'describe_schema':
        return `${base}schema`;
      case 'generate_query_config':
        return snapFailed ? `${base}fixing` : snapSuccess ? `${base}refining` : `${base}generating`;
      default:
        return '$ai.chat.thinking';
    }
  });
  get loading$() {
    return this.store.select(selectAILoading);
  }
  get httpResult$() {
    return this.store.select(selectHttpResult);
  }
}
